import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { withSite } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BatchEditor } from "@/lib/batch/BatchEditor";
import { reconcile, markDuplicates } from "@/lib/batch/batchCore";
import { DatePicker } from "@/components/DatePicker";
import PriceListImport from "@/components/PriceListImport";
import { OrderTemplatesTab } from "@/components/OrderTemplates";
import { Plus, Truck, Send, Trash2, ChevronRight, Package, Repeat, Link2, FileText, Search, Pencil, AlertTriangle, ShieldQuestion, ShieldCheck, Check } from "lucide-react";

const ALLERGEN_LIST = ["celery", "gluten", "crustaceans", "eggs", "fish", "lupin", "milk", "molluscs", "mustard", "nuts", "peanuts", "sesame", "soya", "sulphites"];

const PROV_MAP = {
  supplier_master: ["Supplier price", "bg-slate-100 text-slate-600"],
  account: ["Account price", "bg-blue-50 text-blue-600"],
  local_override: ["Local override", "bg-brand-light text-brand-dark"],
  legacy_catalogue: ["Supplier price", "bg-slate-100 text-slate-600"],
};
function ProvBadge({ source }) {
  const [label, cls] = PROV_MAP[source] || [null, ""];
  if (!label) return null;
  return <span data-testid={`prov-${source}`} className={`inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}
const effPrice = (p) => (p.effective_price?.status === "ok" ? p.effective_price.price : p.price);

function AllergenBadge({ allergens }) {
  const a = allergens || { status: "unknown" };
  if (a.status === "contains") return <span data-testid="allergen-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-50 text-red-600"><AlertTriangle size={11} /> {(a.list || []).join(", ") || "Contains"}</span>;
  if (a.status === "may_contain") return <span data-testid="allergen-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-600"><AlertTriangle size={11} /> May contain</span>;
  if (a.status === "none") return <span data-testid="allergen-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-brand-light text-brand-dark"><ShieldCheck size={11} /> No allergens</span>;
  return <span data-testid="allergen-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"><ShieldQuestion size={11} /> Allergens unknown</span>;
}

function SiteAssign({ venues, value, onChange, testidPrefix }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  if (!venues.length) return null;
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">Sites</Label>
      <p className="text-[11px] text-slate-400 mb-1.5">Leave all unticked to make this available organisation-wide.</p>
      <div className="border rounded-lg p-2 space-y-1 max-h-28 overflow-y-auto">
        {venues.map((v) => (
          <label key={v.venue_id} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid={`${testidPrefix}-site-${v.venue_id}`}>
            <Checkbox checked={value.includes(v.venue_id)} onCheckedChange={() => toggle(v.venue_id)} className="h-4 w-4" /> {v.name}
          </label>
        ))}
      </div>
    </div>
  );
}

function LowStockModal({ suppliers, onClose }) {
  const [f, setF] = useState({ supplier_id: suppliers[0]?.supplier_id || "", product: "", quantity: 1, unit: "each" });
  const save = async () => {
    if (!f.supplier_id || !f.product) return toast.error("Supplier and product required");
    try { const { data } = await api.post("/reorder/flag", f); toast.success(`Added to ${data.target}`); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="low-stock-modal">
        <DialogHeader><DialogTitle>Flag low stock</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Adds the item to the supplier's recurring order, or the next pending order.</p>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Supplier</Label>
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}><SelectTrigger data-testid="low-supplier" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label className="text-xs font-semibold text-slate-600">Product</Label><Input value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} data-testid="low-product" className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Qty</Label><Input type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} data-testid="low-qty" className="mt-1.5" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-low-stock" className="bg-brand hover:bg-brand-hover">Add to reorder</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogModal({ onClose, onSaved }) {
  const [avail, setAvail] = useState(null);
  const load = async () => setAvail((await api.get("/suppliers/global/available")).data);
  React.useEffect(() => { load(); }, []);
  const attach = async (id, name) => {
    try { await api.post("/suppliers/attach", { global_supplier_id: id }); toast.success(`${name} added`); load(); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="catalog-modal">
        <DialogHeader><DialogTitle>Add supplier from global catalog</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {avail === null && <div className="text-sm text-slate-400">Loading…</div>}
          {avail && avail.length === 0 && <div className="text-sm text-slate-400">All catalog suppliers have been added.</div>}
          {avail && avail.map((g) => (
            <div key={g.global_supplier_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`catalog-${g.global_supplier_id}`}>
              <div><div className="font-semibold text-slate-800">{g.name}</div><div className="text-xs text-slate-500">{g.category || "Supplier"} · cutoff {g.cutoff_time}</div></div>
              <Button size="sm" className="bg-brand hover:bg-brand-hover rounded-lg" data-testid={`attach-${g.global_supplier_id}`} onClick={() => attach(g.global_supplier_id, g.name)}>Add</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const { has } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [orders, setOrders] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [spend, setSpend] = useState({});
  const [supModal, setSupModal] = useState(false);
  const [catalogModal, setCatalogModal] = useState(false);
  const [lowModal, setLowModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [prodModal, setProdModal] = useState(false);
  const [batchProd, setBatchProd] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("suppliers");
  const [prodSupplier, setProdSupplier] = useState("all");
  const [priceImport, setPriceImport] = useState(null);
  const [prodRefresh, setProdRefresh] = useState(0);

  const load = async () => {
    const [s, o, r, sp, v] = await Promise.all([
      api.get("/suppliers"), api.get(withSite("/orders")), api.get("/recurring-orders"),
      api.get("/suppliers/spend-summary").catch(() => ({ data: {} })), api.get("/venues").catch(() => ({ data: [] })),
    ]);
    setSuppliers(s.data); setOrders(o.data); setRecurring(r.data); setSpend(sp.data || {}); setVenues(v.data || []);
  };
  useEffect(() => { load(); const h = () => load(); window.addEventListener("cc-site-changed", h); return () => window.removeEventListener("cc-site-changed", h); }, []);

  const pending = orders.filter((o) => ["pending", "draft", "scheduled"].includes(o.status));
  const previous = orders.filter((o) => ["sent", "failed", "cancelled"].includes(o.status));

  return (
    <div>
      <PageHeader title="Ordering" subtitle="Suppliers, product catalogue, orders and recurring standing orders." testid="orders-header"
        action={has("manage_suppliers") && <div className="flex gap-2 flex-wrap">
          {has("create_orders") && <Button variant="outline" className="rounded-xl border-slate-300" data-testid="low-stock-btn" onClick={() => setLowModal(true)}><Package size={16} className="mr-1" /> Flag low stock</Button>}
          <Button variant="outline" className="rounded-xl border-slate-300" data-testid="add-from-catalog-btn" onClick={() => setCatalogModal(true)}><Link2 size={16} className="mr-1" /> Catalog</Button>
          <Button data-testid="add-supplier-btn" onClick={() => setSupModal(true)} className="bg-brand hover:bg-brand-hover rounded-xl"><Plus size={18} className="mr-1" /> Add Supplier</Button>
        </div>} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">Recurring</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers" className="space-y-3">
          {suppliers.length === 0 ? <EmptyState icon={Truck} title="No suppliers added yet." message="Add your first supplier to start ordering." /> :
            suppliers.map((s) => (
              <div key={s.supplier_id} className="cc-list-row flex-wrap gap-2" data-testid={`supplier-${s.supplier_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 rounded-xl bg-brand-light flex items-center justify-center font-display font-bold text-brand-dark">{s.name[0]}</div>
                  <div>
                    <div className="font-semibold text-slate-800 flex items-center gap-2">{s.name}
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{(s.site_ids || []).length ? `${s.site_ids.length} site(s)` : "org-wide"}</span></div>
                    <div className="text-xs text-slate-500">{s.email || "No email"} · cutoff {s.cutoff_time}{spend[s.supplier_id] > 0 && <span className="text-brand-dark font-semibold"> · £{spend[s.supplier_id].toFixed(2)} this month</span>}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {has("manage_suppliers") && <button data-testid={`edit-supplier-${s.supplier_id}`} title="Edit supplier" onClick={() => setSupModal(s)} className="p-2 text-slate-400 hover:text-brand"><Pencil size={16} /></button>}
                  {has("manage_suppliers") && <button data-testid={`statement-${s.supplier_id}`} title="Email monthly statement" onClick={async (e) => { e.stopPropagation(); try { const { data } = await api.post(`/suppliers/${s.supplier_id}/email-statement`, {}); toast.success(`Statement emailed (£${data.total})`); } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } }} className="p-2 text-slate-400 hover:text-brand"><FileText size={16} /></button>}
                  <Button size="sm" variant="ghost" className="rounded-lg" data-testid={`supplier-products-${s.supplier_id}`} onClick={() => { setProdSupplier(s.supplier_id); setTab("products"); }}><Package size={14} className="mr-1" /> Products</Button>
                  {has("create_orders") && <Button size="sm" variant="outline" className="rounded-lg" data-testid={`order-from-${s.supplier_id}`} onClick={() => setOrderModal(s)}>New order</Button>}
                  {has("manage_suppliers") && <button data-testid={`archive-supplier-${s.supplier_id}`} onClick={async () => { await api.delete(`/suppliers/${s.supplier_id}`); toast.success("Supplier archived"); load(); }} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>}
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="products">
          <ProductsTab suppliers={suppliers} venues={venues} canManage={has("manage_suppliers")} onNew={() => setProdModal({})} onEdit={(p) => setProdModal(p)} onBatch={() => setBatchProd(true)} refreshKey={`${prodRefresh}-${!!prodModal}`} supFilter={prodSupplier} setSupFilter={setProdSupplier} onImport={(s) => setPriceImport(s)} />
        </TabsContent>

        <TabsContent value="orders" className="space-y-5">
          <Section title="Pending Orders" rows={pending} setDetail={setDetail} empty="No pending orders." />
          <Section title="Previous Orders" rows={previous} setDetail={setDetail} empty="No previous orders." />
        </TabsContent>

        <TabsContent value="templates">
          {tab === "templates" && <OrderTemplatesTab suppliers={suppliers} venues={venues} has={has} onDraftsCreated={load} />}
        </TabsContent>

        <TabsContent value="recurring" className="space-y-3">
          {recurring.filter(r=>r.active).length === 0 ? <EmptyState icon={Repeat} title="No recurring orders." message="Set up standing orders that generate automatically." /> :
            recurring.filter(r=>r.active).map((r) => (
              <div key={r.recurring_id} className="cc-list-row" data-testid={`recurring-${r.recurring_id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-brand-light flex items-center justify-center"><Repeat className="text-brand-dark" size={20} /></div>
                  <div>
                    <div className="font-semibold text-slate-800">{r.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{r.frequency} · {r.items.length} items · {suppliers.find(s=>s.supplier_id===r.supplier_id)?.name}</div>
                  </div>
                </div>
                <StatusBadge status="scheduled" />
              </div>
            ))}
        </TabsContent>
      </Tabs>

      {supModal && <SupplierModal supplier={typeof supModal === "object" ? supModal : null} venues={venues} onClose={() => setSupModal(false)} onSaved={() => { setSupModal(false); load(); }} />}
      {catalogModal && <CatalogModal onClose={() => setCatalogModal(false)} onSaved={load} />}
      {lowModal && <LowStockModal suppliers={suppliers} onClose={() => setLowModal(false)} />}
      {orderModal && <OrderModal supplier={orderModal} suppliers={suppliers} onClose={() => setOrderModal(false)} onSaved={() => { setOrderModal(false); load(); }} />}
      {prodModal && <ProductModal product={prodModal.product_id ? prodModal : null} suppliers={suppliers} venues={venues} onClose={() => setProdModal(false)} onSaved={() => setProdModal(false)} />}
      {detail && <OrderDetail order={detail} onClose={() => setDetail(null)} onChanged={() => { setDetail(null); load(); }} canSend={has("create_orders")} canEdit={has("edit_orders")} />}
      {priceImport && <PriceListImport supplier={priceImport} onClose={() => setPriceImport(null)} onApproved={() => setProdRefresh((n) => n + 1)} />}
      {batchProd && <BatchAddProducts suppliers={suppliers} onClose={() => setBatchProd(false)} onDone={() => setProdModal((k) => (k === false ? {} : false))} />}
    </div>
  );
}

function ProductsTab({ suppliers, venues, canManage, onNew, onEdit, onBatch, refreshKey, supFilter, setSupFilter, onImport }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const selectedSupplier = suppliers.find((s) => s.supplier_id === supFilter);
  const [aiPriceList, setAiPriceList] = useState(false);
  useEffect(() => { if (canManage) api.get("/ai/features").then((r) => setAiPriceList((r.data.features || []).includes("price_list_extraction"))).catch(() => {}); }, [canManage]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const supName = (id) => suppliers.find((s) => s.supplier_id === id)?.name;

  const load = async () => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (search.trim()) params.set("search", search.trim());
      if (supFilter !== "all") params.set("supplier_id", supFilter);
      const { data } = await api.get(`/products?${params.toString()}`);
      setProducts(data);
    } catch { setError(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, supFilter, refreshKey]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search" className="pl-9" />
        </div>
        <Select value={supFilter} onValueChange={setSupFilter}><SelectTrigger data-testid="product-supplier-filter" className="w-44"><SelectValue placeholder="All suppliers" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All suppliers</SelectItem>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        {canManage && selectedSupplier && <Button variant="outline" disabled={!aiPriceList} title={aiPriceList ? "" : "Enable the Invoice AI module in Settings to import price lists"} className="rounded-xl border-brand text-brand-dark" data-testid="import-price-list-btn" onClick={() => onImport(selectedSupplier)}><FileText size={16} className="mr-1" /> Import Price List</Button>}
        {canManage && <Button variant="outline" className="rounded-xl" data-testid="batch-add-product-btn" onClick={onBatch}><Package size={16} className="mr-1" /> Batch add products</Button>}
        {canManage && <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="add-product-btn" onClick={onNew}><Plus size={16} className="mr-1" /> Add product</Button>}
      </div>
      {loading && products.length === 0 ? <div className="text-sm text-slate-400 p-4">Loading…</div> :
        error ? <div className="cc-card p-6 text-center text-sm" data-testid="products-error"><p className="text-red-500 mb-2">Couldn't load the catalogue.</p><Button variant="outline" size="sm" onClick={load} data-testid="products-retry">Retry</Button></div> :
        products.length === 0 ? <EmptyState icon={Package} title="No products found." message="Add products or adjust your search." /> :
          <div className="space-y-2">
            {products.map((p) => (
              <div key={p.product_id} className="cc-list-row" data-testid={`product-${p.product_id}`} onClick={() => canManage && onEdit(p)}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Package className="text-slate-500" size={18} /></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate flex items-center gap-2">{p.name} {p.sku && <span className="text-[10px] text-slate-400 font-mono">{p.sku}</span>}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                      {supName(p.supplier_id) && <span>{supName(p.supplier_id)}</span>}
                      {p.pack_size && <span>· {p.pack_size}</span>}
                      {p.category && <span>· {p.category}</span>}
                      <AllergenBadge allergens={p.allergens} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {p.global_product_id && p.effective_price?.status !== "ok"
                    ? <span className="text-[11px] font-semibold text-amber-600" data-testid={`product-price-pending-${p.product_id}`}>Price pending</span>
                    : <span className="font-display font-bold text-slate-800" data-testid={`product-price-${p.product_id}`}>{effPrice(p) != null ? `£${Number(effPrice(p)).toFixed(2)}` : "—"}</span>}
                  {p.effective_price?.status === "ok" && <ProvBadge source={p.effective_price.source} />}
                  {canManage && <ChevronRight size={16} className="text-slate-300" />}
                </div>
              </div>
            ))}
          </div>}
    </div>
  );
}

function ProductModal({ product, suppliers, venues, onClose, onSaved }) {
  const editing = !!product;
  const [f, setF] = useState(product ? {
    name: product.name || "", sku: product.sku || "", brand: product.brand || "", category: product.category || "",
    pack_size: product.pack_size || "", purchase_unit: product.purchase_unit || "each", price: product.price ?? "",
    vat_rate: product.vat_rate ?? "", supplier_id: product.supplier_id || "none", notes: product.notes || "",
    site_ids: product.site_ids || [], allergen_status: product.allergens?.status || "unknown", allergen_list: product.allergens?.list || [],
  } : { name: "", sku: "", brand: "", category: "", pack_size: "", purchase_unit: "each", price: "", vat_rate: "", supplier_id: "none", notes: "", site_ids: [], allergen_status: "unknown", allergen_list: [] });

  const toggleAllergen = (a) => setF((s) => ({ ...s, allergen_list: s.allergen_list.includes(a) ? s.allergen_list.filter((x) => x !== a) : [...s.allergen_list, a] }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Product name required");
    const payload = {
      name: f.name, sku: f.sku, brand: f.brand, category: f.category, pack_size: f.pack_size,
      purchase_unit: f.purchase_unit, notes: f.notes, site_ids: f.site_ids,
      supplier_id: f.supplier_id === "none" ? null : f.supplier_id,
      price: f.price === "" ? null : Number(f.price), vat_rate: f.vat_rate === "" ? null : Number(f.vat_rate),
      allergens: { status: f.allergen_status, list: f.allergen_status === "contains" || f.allergen_status === "may_contain" ? f.allergen_list : [], source: "manual" },
    };
    try {
      if (editing) await api.put(`/products/${product.product_id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="product-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Field label="Product name" v={f.name} set={(v) => setF({ ...f, name: v })} testid="product-name" /></div>
            <Field label="SKU / code" v={f.sku} set={(v) => setF({ ...f, sku: v })} testid="product-sku" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Brand" v={f.brand} set={(v) => setF({ ...f, brand: v })} testid="product-brand" />
            <Field label="Category" v={f.category} set={(v) => setF({ ...f, category: v })} testid="product-category" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Pack size" v={f.pack_size} set={(v) => setF({ ...f, pack_size: v })} testid="product-pack" />
            <Field label="Unit" v={f.purchase_unit} set={(v) => setF({ ...f, purchase_unit: v })} testid="product-unit" />
            <Field label="Price £" type="number" v={f.price} set={(v) => setF({ ...f, price: v })} testid="product-price-input" />
            <Field label="VAT %" type="number" v={f.vat_rate} set={(v) => setF({ ...f, vat_rate: v })} testid="product-vat" />
          </div>
          <div><Label className="text-xs font-semibold text-slate-600">Preferred supplier</Label>
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}><SelectTrigger data-testid="product-supplier" className="mt-1.5"><SelectValue placeholder="No supplier" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No supplier</SelectItem>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Allergen status</Label>
            <Select value={f.allergen_status} onValueChange={(v) => setF({ ...f, allergen_status: v })}><SelectTrigger data-testid="product-allergen-status" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unknown">Unknown (not yet confirmed)</SelectItem>
                <SelectItem value="none">No allergens</SelectItem>
                <SelectItem value="contains">Contains allergens</SelectItem>
                <SelectItem value="may_contain">May contain</SelectItem>
              </SelectContent></Select>
            {(f.allergen_status === "contains" || f.allergen_status === "may_contain") && (
              <div className="grid grid-cols-2 gap-1.5 mt-2 border rounded-lg p-2 max-h-32 overflow-y-auto">
                {ALLERGEN_LIST.map((a) => <label key={a} className="flex items-center gap-2 text-xs text-slate-600 capitalize cursor-pointer" data-testid={`allergen-${a}`}><Checkbox checked={f.allergen_list.includes(a)} onCheckedChange={() => toggleAllergen(a)} className="h-4 w-4" /> {a}</label>)}
              </div>
            )}
          </div>
          <SiteAssign venues={venues} value={f.site_ids} onChange={(v) => setF({ ...f, site_ids: v })} testidPrefix="product" />
          <Field label="Notes" v={f.notes} set={(v) => setF({ ...f, notes: v })} testid="product-notes" />
          {editing && product.global_product_id && (
            <LocalOverridePanel product={product} onSaved={onSaved} />
          )}
          {editing && product.price_history?.length > 1 && (
            <div className="border-t pt-2"><div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Price history</div>
              {product.price_history.slice(-5).reverse().map((h, i) => <div key={i} className="text-xs text-slate-500">£{Number(h.price).toFixed(2)} · {(h.effective_at || "").slice(0, 10)} · {h.source}</div>)}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-product" className="bg-brand hover:bg-brand-hover">{editing ? "Save" : "Add product"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchAddProducts({ suppliers, onClose, onDone }) {
  const [supplierId, setSupplierId] = useState("none");
  const [existing, setExisting] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams({ limit: "500" });
    if (supplierId !== "none") params.set("supplier_id", supplierId);
    setLoaded(false);
    api.get(`/products?${params.toString()}`)
      .then((r) => setExisting(r.data))
      .catch(() => setExisting([]))
      .finally(() => setLoaded(true));
  }, [supplierId]);

  const columns = [
    { key: "name", label: "Product name", placeholder: "e.g. Plain Flour" },
    { key: "sku", label: "SKU / code", placeholder: "optional" },
    { key: "pack_size", label: "Pack", placeholder: "e.g. 16kg" },
    { key: "price", label: "Price £", placeholder: "blank = missing" },
    { key: "vat_rate", label: "VAT %", placeholder: "optional" },
    { key: "category", label: "Category", placeholder: "optional" },
  ];
  const validateRow = (v) => {
    const errors = {};
    if (!String(v.name || "").trim()) errors.name = "Required";
    const p = String(v.price ?? "").replace(/^[£$€]\s*/, "").replace(/,/g, "").trim();
    if (p !== "" && (isNaN(Number(p)) || Number(p) < 0)) errors.price = "Bad price";
    const vt = String(v.vat_rate ?? "").replace("%", "").trim();
    if (vt !== "" && (isNaN(Number(vt)) || Number(vt) < 0 || Number(vt) > 100)) errors.vat_rate = "0–100";
    return { errors, warnings: [] };
  };
  // Duplicate context: exact SKU where present, else exact product name (within the chosen supplier scope).
  const existingSkus = existing.filter((p) => p.sku).map((p) => p.sku);
  const existingNames = existing.map((p) => p.name);
  const annotate = (rows) => {
    const bySku = markDuplicates(rows, { field: "sku", existing: existingSkus, normalize: (s) => String(s || "").replace(/\s+/g, "").toLowerCase() });
    // Name-based warning only for rows without a SKU.
    const seenNames = new Set(existingNames.map((n) => String(n || "").replace(/\s+/g, " ").trim().toLowerCase()));
    const seenBatch = new Set();
    return bySku.map((r) => {
      if (String(r.values.sku || "").trim()) return r;
      const key = String(r.values.name || "").replace(/\s+/g, " ").trim().toLowerCase();
      const warns = (r.warnings || []).filter((w) => w !== "Possible duplicate");
      const dup = !!key && (seenNames.has(key) || seenBatch.has(key));
      if (key) seenBatch.add(key);
      if (dup && r.status !== "invalid" && r.status !== "created") return { ...r, warnings: [...warns, "Possible duplicate"], status: "warning" };
      return { ...r, warnings: warns };
    });
  };
  const aliasMap = {
    name: ["product", "product name", "item", "description", "name"],
    sku: ["sku", "product code", "supplier code", "item code", "code"],
    pack_size: ["pack", "pack size", "pack description"],
    price: ["price", "cost", "unit price"],
    vat_rate: ["vat", "vat rate"],
    category: ["category"],
  };
  const onCreate = async (subset, allRows) => {
    const parsePrice = (v) => { const s = String(v ?? "").replace(/^[£$€]\s*/, "").replace(/,/g, "").trim(); return s === "" ? null : Number(s); };
    const payloadRows = subset.map((r) => ({
      name: String(r.values.name || "").trim(), sku: String(r.values.sku || "").trim(),
      pack_size: r.values.pack_size || "", price: parsePrice(r.values.price),
      vat_rate: String(r.values.vat_rate ?? "").trim() === "" ? null : Number(String(r.values.vat_rate).replace("%", "").trim()),
      category: r.values.category || "",
    }));
    const operation_id = (crypto.randomUUID && crypto.randomUUID()) || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try {
      const { data } = await api.post("/products/batch", { operation_id, supplier_id: supplierId === "none" ? null : supplierId, rows: payloadRows });
      const next = reconcile(allRows, subset, data.results || []);
      const parts = [`${data.created} created`];
      if (data.duplicates) parts.push(`${data.duplicates} duplicate`);
      if (data.failed) parts.push(`${data.failed} invalid`);
      const msg = parts.join(", ");
      const missing = payloadRows.filter((p, i) => (subset[i]) && p.price === null && data.results?.[i]?.status === "created").length;
      if (data.created > 0) { toast.success(`Batch: ${msg}${missing ? ` · ${missing} missing price` : ""}`); onDone && onDone(); }
      else toast.message(`Batch: ${msg}`);
      return next;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch add failed");
      return null;
    }
  };
  if (!loaded) return null;
  const supName = supplierId === "none" ? "No supplier (local)" : (suppliers.find((s) => s.supplier_id === supplierId)?.name || "");
  return (
    <div>
      <div className="fixed inset-0 z-[60] flex items-start justify-center pt-6 pointer-events-none">
        <div className="bg-white rounded-xl shadow-lg border px-4 py-2 pointer-events-auto flex items-center gap-2" data-testid="batch-product-supplier-bar">
          <span className="text-xs text-slate-500">Supplier:</span>
          <Select value={supplierId} onValueChange={setSupplierId}><SelectTrigger data-testid="batch-product-supplier" className="w-56 h-8"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">No supplier (local)</SelectItem>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <BatchEditor
        columns={columns} validateRow={validateRow} annotate={annotate} pasteColumn="name" aliasMap={aliasMap}
        destinationLabel={supName} notice="Products with a blank price stay 'missing price' — costing stays incomplete until a price is added. Allergens remain UNKNOWN until set."
        createLabel="Create products" onCreate={onCreate} onClose={onClose} />
    </div>
  );
}

function LocalOverridePanel({ product, onSaved }) {
  const ov = product.local_override;
  const [price, setPrice] = useState(ov?.price ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (price === "" || Number(price) <= 0) return toast.error("Enter a price greater than 0");
    if (!reason.trim()) return toast.error("A reason is required for a local price override");
    setBusy(true);
    try {
      await api.post(`/products/${product.product_id}/local-price`, { price: Number(price), reason: reason.trim() });
      toast.success("Local price override saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/products/${product.product_id}/local-price`); toast.success("Override removed — reverts to account/supplier price"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-3" data-testid="local-override-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-bold uppercase text-slate-400">Local price override</div>
        <ProvBadge source={product.effective_price?.source} />
      </div>
      <p className="text-[11px] text-slate-400 mb-2">Linked to the CaterCTRL catalogue. Set a local price to override the account/supplier price for your organisation only.</p>
      {ov && (
        <div className="text-xs text-slate-600 mb-2 flex items-center justify-between bg-brand-light/40 rounded-lg px-2 py-1.5" data-testid="local-override-active">
          <span>Active: <b>£{Number(ov.price).toFixed(2)}</b> · {ov.reason}{ov.set_by?.name ? ` · ${ov.set_by.name}` : ""}</span>
          <button type="button" data-testid="remove-local-override" onClick={remove} disabled={busy} className="text-red-500 text-xs font-semibold">Remove</button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 items-end">
        <div><Label className="text-xs font-semibold text-slate-600">Local price £</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="local-override-price" className="mt-1.5 h-9" /></div>
        <div className="col-span-2"><Label className="text-xs font-semibold text-slate-600">Reason (required)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="local-override-reason" placeholder="e.g. negotiated local price" className="mt-1.5 h-9" /></div>
      </div>
      <Button size="sm" onClick={save} disabled={busy} data-testid="save-local-override" className="mt-2 bg-brand hover:bg-brand-hover">{ov ? "Update override" : "Set local price"}</Button>
    </div>
  );
}

function Section({ title, rows, setDetail, empty }) {
  return (
    <div>
      <h3 className="font-display font-bold text-slate-700 mb-2">{title}</h3>
      {rows.length === 0 ? <div className="cc-card p-5 text-sm text-slate-400">{empty}</div> :
        <div className="space-y-2">{rows.map((o) => (
          <div key={o.order_id} className="cc-list-row" data-testid={`order-${o.order_id}`} onClick={() => setDetail(o)}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center"><Package className="text-slate-500" size={18} /></div>
              <div>
                <div className="font-semibold text-slate-800">{o.supplier_name}</div>
                <div className="text-xs text-slate-500">{o.items.length} items · delivery {o.delivery_date || "—"}</div>
              </div>
            </div>
            <div className="flex items-center gap-2"><StatusBadge status={o.status} /><ChevronRight size={16} className="text-slate-300" /></div>
          </div>
        ))}</div>}
    </div>
  );
}

function SupplierModal({ supplier, venues, onClose, onSaved }) {
  const editing = !!supplier;
  const [f, setF] = useState(supplier ? {
    name: supplier.name || "", email: supplier.email || "", phone: supplier.phone || "", contact_name: supplier.contact_name || "",
    cutoff_time: supplier.cutoff_time || "12:00", account_ref: supplier.account_ref || "", website: supplier.website || "",
    min_order: supplier.min_order || "", notes: supplier.notes || "", site_ids: supplier.site_ids || [],
  } : { name: "", email: "", phone: "", contact_name: "", cutoff_time: "12:00", account_ref: "", website: "", min_order: "", notes: "", site_ids: [] });
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      if (editing) await api.put(`/suppliers/${supplier.supplier_id}`, f);
      else await api.post("/suppliers", f);
      toast.success(editing ? "Supplier updated" : "Supplier added"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="supplier-modal">
        <DialogHeader><DialogTitle>{editing ? "Edit supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <Field label="Supplier name" v={f.name} set={(v) => setF({ ...f, name: v })} testid="supplier-name" />
          <Field label="Order email" type="email" v={f.email} set={(v) => setF({ ...f, email: v })} testid="supplier-email" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact name" v={f.contact_name} set={(v) => setF({ ...f, contact_name: v })} testid="supplier-contact" />
            <Field label="Cutoff time" type="time" v={f.cutoff_time} set={(v) => setF({ ...f, cutoff_time: v })} testid="supplier-cutoff" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" v={f.phone} set={(v) => setF({ ...f, phone: v })} testid="supplier-phone" />
            <Field label="Account ref" v={f.account_ref} set={(v) => setF({ ...f, account_ref: v })} testid="supplier-account-ref" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Website" v={f.website} set={(v) => setF({ ...f, website: v })} testid="supplier-website" />
            <Field label="Min order £" v={f.min_order} set={(v) => setF({ ...f, min_order: v })} testid="supplier-min-order" />
          </div>
          <SiteAssign venues={venues} value={f.site_ids} onChange={(v) => setF({ ...f, site_ids: v })} testidPrefix="supplier" />
          <Field label="Notes" v={f.notes} set={(v) => setF({ ...f, notes: v })} testid="supplier-notes" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-supplier" className="bg-brand hover:bg-brand-hover">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderModal({ supplier, suppliers, onClose, onSaved }) {
  const [supplierId, setSupplierId] = useState(supplier?.supplier_id || suppliers[0]?.supplier_id);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [autoSend, setAutoSend] = useState(true);
  const [items, setItems] = useState([{ product: "", quantity: 1, unit: "each", price: "", notes: "" }]);
  const upd = (i, k, v) => { const n = [...items]; n[i][k] = v; setItems(n); };
  const total = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const save = async (status) => {
    const clean = items.filter((it) => it.product.trim()).map((it) => ({ ...it, price: it.price === "" ? null : Number(it.price) }));
    if (!clean.length) return toast.error("Add at least one product");
    await api.post("/orders", { supplier_id: supplierId, delivery_date: deliveryDate, notes, items: clean, status, auto_send: status === "draft" ? false : autoSend });
    toast.success(status === "draft" ? "Saved as draft" : "Order created"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="order-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>Create order</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label className="text-xs font-semibold text-slate-600">Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}><SelectTrigger data-testid="order-supplier" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs font-semibold text-slate-600">Delivery date</Label><div className="mt-1.5"><DatePicker value={deliveryDate} onChange={setDeliveryDate} placeholder="Pick delivery date" testid="order-delivery" className="w-full" /></div></div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Products</Label>
            <div className="space-y-2 mt-1.5">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`order-item-${i}`}>
                  <Input className="col-span-4" placeholder="Product" data-testid={`item-product-${i}`} value={it.product} onChange={(e) => upd(i, "product", e.target.value)} />
                  <Input className="col-span-2" type="number" placeholder="Qty" data-testid={`item-qty-${i}`} value={it.quantity} onChange={(e) => upd(i, "quantity", Number(e.target.value))} />
                  <Input className="col-span-2" placeholder="Unit" data-testid={`item-unit-${i}`} value={it.unit} onChange={(e) => upd(i, "unit", e.target.value)} />
                  <Input className="col-span-2" type="number" placeholder="£ ea" data-testid={`item-price-${i}`} value={it.price} onChange={(e) => upd(i, "price", e.target.value)} />
                  <Input className="col-span-2" placeholder="Notes" data-testid={`item-notes-${i}`} value={it.notes} onChange={(e) => upd(i, "notes", e.target.value)} />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2">
              <Button variant="outline" size="sm" className="rounded-lg" data-testid="add-order-item" onClick={() => setItems([...items, { product: "", quantity: 1, unit: "each", price: "", notes: "" }])}><Plus size={14} className="mr-1" /> Add product</Button>
              <span className="text-sm font-semibold text-slate-700" data-testid="order-total">Total: £{total.toFixed(2)}</span>
            </div>
          </div>
          <Field label="Notes" v={notes} set={setNotes} testid="order-notes" />
          <div className="flex items-center justify-between bg-brand-light/60 rounded-xl px-3.5 py-3">
            <div><div className="text-sm font-semibold text-slate-700">Auto-send at supplier cutoff</div><div className="text-[11px] text-slate-500">Emails the supplier automatically once their cutoff time passes</div></div>
            <Switch checked={autoSend} onCheckedChange={setAutoSend} data-testid="order-auto-send" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => save("draft")} data-testid="save-draft">Save draft</Button>
          <Button onClick={() => save("pending")} data-testid="submit-order" className="bg-brand hover:bg-brand-hover">Submit order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetail({ order, onClose, onChanged, canSend, canEdit }) {
  const [items, setItems] = useState(order.items || []);
  const [dirty, setDirty] = useState(false);
  const unresolved = items.filter((it) => it.needs_confirmation).length;
  const send = async () => {
    try { await api.post(`/orders/${order.order_id}/send`); toast.success("Order emailed to supplier"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); onChanged(); }
  };
  const persist = async (next) => {
    setItems(next); setDirty(true);
    try { await api.put(`/orders/${order.order_id}`, { items: next }); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const updItem = (i, patch) => setItems(items.map((it, x) => x === i ? { ...it, ...patch } : it));
  const confirmItem = async (i) => {
    const it = items[i];
    if (!String(it.product || "").trim()) return toast.error("Enter a product/description");
    if (it.quantity == null || String(it.quantity).trim() === "" || Number.isNaN(Number(it.quantity))) return toast.error("Enter a quantity to confirm");
    if (!String(it.unit || "").trim()) return toast.error("Enter a unit to confirm");
    await persist(items.map((x, idx) => idx === i ? { ...x, product: String(x.product).trim(), quantity: Number(x.quantity), unit: String(x.unit).trim(), needs_confirmation: false } : x));
    toast.success("Item confirmed");
  };
  const removeItem = async (i) => {
    if (!window.confirm("Remove this item from the order?")) return;
    await persist(items.filter((_, x) => x !== i));
    toast.success("Item removed");
  };
  return (
    <Dialog open onOpenChange={() => (dirty ? onChanged() : onClose())}>
      <DialogContent data-testid="order-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{order.supplier_name} <StatusBadge status={order.status} /></DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 space-y-1">
          <div>Delivery date: <b>{order.delivery_date || "—"}</b></div>
          <div>Created by: {order.created_by_name}</div>
          {order.sent_at && <div>Sent to {order.sent_to} · {fmtDateTime(order.sent_at)}</div>}
        </div>
        {unresolved > 0 && <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="order-held-warning">{unresolved} item{unresolved > 1 ? "s" : ""} need confirmation. This order will NOT auto-send until every item is confirmed or removed.</div>}
        <div className="border rounded-xl divide-y mt-2">
          {items.map((it, i) => (
            it.needs_confirmation && canEdit && ["pending", "draft", "scheduled"].includes(order.status) ? (
              <div key={i} className="p-3 space-y-2" data-testid={`order-item-confirm-${i}`}>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`needs-confirmation-${i}`}><AlertTriangle size={11} /> Needs confirmation</span>
                <div className="grid grid-cols-6 gap-2">
                  <Input className="col-span-3" value={it.product || ""} onChange={(e) => updItem(i, { product: e.target.value })} data-testid={`item-product-${i}`} placeholder="Product / description" />
                  <Input className="col-span-1" type="number" value={it.quantity ?? ""} onChange={(e) => updItem(i, { quantity: e.target.value })} data-testid={`item-qty-${i}`} placeholder="Qty" />
                  <Input className="col-span-2" value={it.unit || ""} onChange={(e) => updItem(i, { unit: e.target.value })} data-testid={`item-unit-${i}`} placeholder="Unit" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" className="text-red-600" data-testid={`remove-item-${i}`} onClick={() => removeItem(i)}><Trash2 size={14} className="mr-1" /> Remove</Button>
                  <Button size="sm" className="bg-brand hover:bg-brand-hover" data-testid={`confirm-item-${i}`} onClick={() => confirmItem(i)}><Check size={14} className="mr-1" /> Confirm</Button>
                </div>
              </div>
            ) : (
              <div key={i} className="p-3 flex justify-between items-center text-sm" data-testid={`order-item-${i}`}>
                <span>{it.product} <span className="text-slate-400">{it.notes}</span>{it.needs_confirmation && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"><AlertTriangle size={11} /> Needs confirmation</span>}</span>
                <b>{it.quantity != null ? it.quantity : "—"} {it.unit || ""}{it.price ? ` · £${(it.price * it.quantity).toFixed(2)}` : ""}</b>
              </div>
            )
          ))}
        </div>
        {items.some((it) => it.price) && <div className="text-right font-bold text-slate-800 mt-1" data-testid="order-detail-total">Total: £{items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0).toFixed(2)}</div>}
        <DialogFooter>
          {canEdit && ["pending", "draft"].includes(order.status) && <Button variant="outline" data-testid="cancel-order" onClick={async () => { await api.post(`/orders/${order.order_id}/cancel`); toast.success("Cancelled"); onChanged(); }}>Cancel order</Button>}
          {canSend && ["pending", "draft", "scheduled", "failed"].includes(order.status) && <Button onClick={send} data-testid="send-order" disabled={unresolved > 0} title={unresolved > 0 ? "Confirm all items before sending" : ""} className="bg-brand hover:bg-brand-hover"><Send size={16} className="mr-1" /> Send to supplier</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, set, type = "text", testid }) {
  return <div><Label className="text-xs font-semibold text-slate-600">{label}</Label><Input type={type} value={v} onChange={(e) => set(e.target.value)} data-testid={testid} className="mt-1.5" /></div>;
}
